import React, { useCallback, useEffect, useState } from "react";
import {
    Button,
    Grid,
    Typography,
    Stack,
    Divider,
    Chip,
    Box,
    IconButton,
} from "@mui/material";
import { color } from "../../utils/constant";
import RHFAutocomplete from "../../components/shared/RHFAutocomplete";
import { useForm } from "react-hook-form";
import MaterialUIPickers from "../../components/shared/DesktopDatePicker";
import { format, startOfDay, isBefore } from "date-fns";
import useDoctorSlotBlockForm from "../../data/talons/DoctorSlotBlock/useDoctorSlotBlockForm";
import { enqueueSnackbar } from "notistack";
import Iconify from "../../components/iconify/iconify";

export default function DoctorSlotBlockForm(props) {
    const { control, setValue, getValues, handleSubmit } = useForm({
        defaultValues: {
            doctor_name: props.editData?.doctor_name || "",
            location_name: props.editData?.area_name || "",
        }
    });

    const {
        doctorList,
        areaList,
        fetchDoctorComboList,
        fetchAreaComboList,
        getAppointmentSlots,
        saveBlockedSlots,
        loading,
    } = useDoctorSlotBlockForm();

    const [doctorId, setDoctorId] = useState("");
    const [locationId, setLocationId] = useState("");
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [slotData, setSlotData] = useState(null);

    // States for selecting slots to block
    const [selectedMorningSlots, setSelectedMorningSlots] = useState([]);
    const [selectedEveningSlots, setSelectedEveningSlots] = useState([]);
    const [initialBlockedTimes, setInitialBlockedTimes] = useState([]);

    const isEdit = !!props.editData;

    useEffect(() => {
        fetchDoctorComboList();
        fetchAreaComboList();
    }, [fetchDoctorComboList, fetchAreaComboList]);

    useEffect(() => {
        const fetchEditData = async () => {
            if (props.editData) {
                let freshEditData = props.editData;

                setDoctorId(freshEditData.doctor_id || props.editData.doctor_id);
                setLocationId(freshEditData.area_id || props.editData.area_id);

                // Using props.editData as fallback if freshEditData lacks names
                setValue("doctor_name", freshEditData.doctor_name || props.editData.doctor_name, { shouldValidate: true, shouldDirty: true });
                setValue("location_name", freshEditData.area_name || freshEditData.location_name || props.editData.area_name, { shouldValidate: true, shouldDirty: true });

                const dateObj = new Date(freshEditData.block_date || props.editData.block_date);
                setSelectedDate(dateObj);

                const formattedDate = format(dateObj, "yyyy-MM-dd");
                const responseData = await getAppointmentSlots({
                    doctor_id: freshEditData.doctor_id || props.editData.doctor_id,
                    location_id: freshEditData.area_id || props.editData.area_id,
                    date: formattedDate,
                });

                let sData = null;
                if (responseData && responseData.length > 0) {
                    sData = responseData[0];
                } else if (responseData?.data && responseData.data.length > 0) {
                    sData = responseData.data[0];
                }

                if (sData) {
                    setSlotData(sData);

                    let blockedTimes = [];
                    const jsonVal = freshEditData.slots_json || freshEditData.block_time || props.editData.slots_json || props.editData.block_time || "";
                    try {
                        if (typeof jsonVal === 'string' && (jsonVal.startsWith('[') || jsonVal.startsWith('{'))) {
                            const parsed = JSON.parse(jsonVal);
                            if (Array.isArray(parsed)) {
                                blockedTimes = parsed.map(s => s.slot_time || s);
                            }
                        } else if (typeof jsonVal === 'string' && jsonVal.trim() !== "") {
                            // If the backend returns raw strings like "10:45 AM", safely extract them via commas
                            blockedTimes = jsonVal.split(',').map(s => s.trim()).filter(Boolean);
                        } else if (Array.isArray(jsonVal)) {
                            blockedTimes = jsonVal.map(s => s.slot_time || s);
                        }
                    } catch (e) {
                        if (typeof jsonVal === 'string') {
                            blockedTimes = jsonVal.split(',').map(s => s.trim()).filter(Boolean);
                        }
                    }

                    console.log("Extracted Initial Blocked Times:", blockedTimes);

                    setInitialBlockedTimes(blockedTimes);
                    setSelectedMorningSlots(blockedTimes.filter(t => sData.morning_slots?.some(m => m.morning_time === t)));
                    setSelectedEveningSlots(blockedTimes.filter(t => sData.evening_slots?.some(e => (e.evening_time || e.morning_time) === t)));
                }
            }
        };
        fetchEditData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.editData, setValue, getAppointmentSlots]);

    const findDoctorFromLabel = useCallback(
        (value) => {
            const selected = value ? doctorList.find((item) => item.label === value)?.id : null;
            setDoctorId(selected);
        },
        [doctorList]
    );

    const findLocationFromLabel = useCallback(
        (value) => {
            const selected = value ? areaList.find((item) => item.label === value)?.id : null;
            setLocationId(selected);
        },
        [areaList]
    );

    const handleDateChange = useCallback((newDate) => {
        if (newDate) setSelectedDate(newDate);
    }, []);

    const onGetSlots = async () => {
        if (!doctorId || !locationId || !selectedDate) {
            enqueueSnackbar("Please select Doctor, Location, and Date.", { variant: "warning" });
            return;
        }
        if (isBefore(startOfDay(selectedDate), startOfDay(new Date()))) {
            enqueueSnackbar("Cannot block slots for past dates. Please select today or a future date.", { variant: "warning" });
            return;
        }
        const formattedDate = format(selectedDate, "yyyy-MM-dd");
        const responseData = await getAppointmentSlots({
            doctor_id: doctorId,
            location_id: locationId,
            date: formattedDate,
        });

        if (responseData && responseData.length > 0) {
            setSlotData(responseData[0]);
            setSelectedMorningSlots([]);
            setSelectedEveningSlots([]);
        } else if (responseData?.data && responseData.data.length > 0) {
            setSlotData(responseData.data[0]);
            setSelectedMorningSlots([]);
            setSelectedEveningSlots([]);
        } else {
            setSlotData(null);
            enqueueSnackbar("No slots found for selected criteria", { variant: "info" });
        }
    };

    const toggleMorningSlot = (slot) => {
        setSelectedMorningSlots(prev =>
            prev.includes(slot.morning_time)
                ? prev.filter(t => t !== slot.morning_time)
                : [...prev, slot.morning_time]
        );
    };

    const toggleEveningSlot = (slot) => {
        const fallbackTime = slot.evening_time || slot.morning_time;
        setSelectedEveningSlots(prev =>
            prev.includes(fallbackTime)
                ? prev.filter(t => t !== fallbackTime)
                : [...prev, fallbackTime]
        );
    };

    const onSave = async () => {
        if (selectedMorningSlots.length === 0 && selectedEveningSlots.length === 0) {
            enqueueSnackbar("Please select at least one slot to block.", { variant: "warning" });
            return;
        }
        if (isBefore(startOfDay(selectedDate), startOfDay(new Date()))) {
            enqueueSnackbar("Cannot block slots for past dates. Please select today or a future date.", { variant: "warning" });
            return;
        }

        const success = await saveBlockedSlots({
            doctor_id: doctorId,
            location_id: locationId,
            date: format(selectedDate, "yyyy-MM-dd"),
            blocked_morning_slots: selectedMorningSlots,
            blocked_evening_slots: selectedEveningSlots
        });

        if (success) {
            onGetSlots(); // Refresh slots
            if (props.postSubmit) props.postSubmit();
        }
    };

    return (
        <React.Fragment>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="h5">
                    Doctor Slot Block
                </Typography>
                <IconButton
                    onClick={() => { if (props.postSubmit) props.postSubmit(); }}
                    sx={{
                        color: 'text.secondary',
                        '&:hover': { bgcolor: 'error.lighter', color: 'error.main' },
                        transition: 'all 0.2s',
                    }}
                >
                    <Iconify icon="eva:close-fill" width={24} />
                </IconButton>
            </Stack>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2}>
                <Grid item xs={12} sm={3} md={3}>
                    <RHFAutocomplete
                        borderRadius={"12px"}
                        isRequired={true}
                        disabled={isEdit}
                        name="doctor_name"
                        label="Doctor Name"
                        freeSolo
                        data={doctorList || []}
                        onInputChange={(value) => findDoctorFromLabel(value)}
                        id={doctorList?.map((option) => option.id)}
                        renderOption={(props, option) => (
                            <li {...props} key={option}>{option}</li>
                        )}
                        control={control}
                        setValue={setValue}
                    />
                </Grid>

                <Grid item xs={12} sm={3} md={3}>
                    <RHFAutocomplete
                        borderRadius={"12px"}
                        isRequired={true}
                        disabled={isEdit}
                        name="location_name"
                        label="Location Name"
                        freeSolo
                        data={areaList || []}
                        onInputChange={(value) => findLocationFromLabel(value)}
                        id={areaList?.map((option) => option.id)}
                        renderOption={(props, option) => (
                            <li {...props} key={option}>{option}</li>
                        )}
                        control={control}
                        setValue={setValue}
                    />
                </Grid>

                <Grid item xs={12} sm={3} md={3}>
                    <MaterialUIPickers
                        required
                        disabled={isEdit}
                        onChange={handleDateChange}
                        id="slot_date"
                        label="Date"
                        type="date"
                        value={selectedDate}
                        minDate={new Date()}
                        size={"small"}
                        sx={{ width: "100%", "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>

                <Grid item xs={12} sm={3} md={3} sx={{ mt: { xs: 1, sm: 0 }, display: 'flex' }}>
                    <Button
                        variant="contained"
                        onClick={onGetSlots}
                        disabled={loading}
                        fullWidth
                        sx={{
                            height: 40,
                            borderRadius: "12px",
                            backgroundColor: color.button,
                            textTransform: 'none',
                            fontWeight: 'bold',
                            boxShadow: '0 4px 14px 0 rgba(0,118,255,0.39)',
                            "&:hover": {
                                backgroundColor: color.button,
                                boxShadow: '0 6px 20px rgba(0,118,255,0.23)',
                            },
                        }}
                    >
                        Get Slots
                    </Button>
                </Grid>

                {slotData && (
                    <Grid item xs={12}>
                        <Box sx={{ mt: 2, p: 3, border: '1px solid #e0e0e0', borderRadius: "16px", backgroundColor: '#fcfcfc' }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                {slotData.week_day && (
                                    <Typography variant="h6" sx={{ color: color.pinkText, fontWeight: 600 }}>
                                        Day: {slotData.week_day}
                                    </Typography>
                                )}
                                {slotData.slot_timing && (
                                    <Chip label={`${slotData.slot_timing} Mins Slot`} color="primary" variant="soft" size="small" />
                                )}
                            </Stack>

                            <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{ width: 16, height: 16, borderRadius: '4px', border: '1px solid #e0e0e0', bgcolor: 'white', mr: 1 }} />
                                    <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary' }}>Available</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{ width: 16, height: 16, borderRadius: '4px', bgcolor: '#CBD5E1', mr: 1 }} />
                                    <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary' }}>Booked</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{ width: 16, height: 16, borderRadius: '4px', bgcolor: '#FECACA', mr: 1 }} />
                                    <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary' }}>Past</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{ width: 16, height: 16, borderRadius: '4px', bgcolor: '#000000e6', mr: 1 }} />
                                    <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary' }}>Blocked</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{ width: 16, height: 16, borderRadius: '4px', bgcolor: 'error.main', mr: 1 }} />
                                    <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary' }}>Selected</Typography>
                                </Box>
                            </Stack>

                            {slotData.message && (
                                <Box sx={{ mb: 2, p: 1.5, borderRadius: '8px', backgroundColor: '#fff5f5', borderLeft: '4px solid #ff4d4f' }}>
                                    <Typography variant="body2" color="error.main" sx={{ fontWeight: 500 }}>
                                        {slotData.message}
                                    </Typography>
                                </Box>
                            )}

                            <Typography variant="subtitle2" sx={{ color: 'primary.main', mb: 1.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                                Morning Slots {slotData.morning_slots?.length > 0 ? `(${slotData.slot_timing} Mins)` : ''}
                            </Typography>

                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                                gap: 1.5,
                                mb: 4
                            }}>
                                {slotData.morning_slots?.map((slot, idx) => {
                                    const isSelected = selectedMorningSlots.includes(slot.morning_time);
                                    const isInitialBlocked = initialBlockedTimes.includes(slot.morning_time);

                                    // Logic for different states
                                    const isBooked = slot.is_booked === 1;
                                    const isPast = slot.is_booked === 2;
                                    const isBlocked = slot.is_booked === 3;

                                    // Determine colors
                                    let bgColor = isSelected ? "error.main" : "white";
                                    let textColor = isSelected ? "white" : "inherit";
                                    let borderColor = isSelected ? "error.main" : "divider";
                                    let isDisabled = false;

                                    if (isBooked && !isInitialBlocked) {
                                        bgColor = '#CBD5E1';
                                        textColor = '#475569';
                                        borderColor = '#CBD5E1';
                                        isDisabled = true;
                                    } else if (isPast && !isInitialBlocked) {
                                        bgColor = '#FECACA';
                                        textColor = '#991B1B';
                                        borderColor = '#FECACA';
                                        isDisabled = true;
                                    } else if (isBlocked && !isInitialBlocked && !isSelected) {
                                        bgColor = '#000000e6';
                                        textColor = 'white';
                                        borderColor = '#000000e6';
                                        // Blocked slots should be selectable only if we are in "unblocking" mode or handling current block session
                                        // For now, let's keep them disabled if they are truly blocked by others
                                        isDisabled = true;
                                    }

                                    return (
                                        <Chip
                                            key={`morning-${idx}`}
                                            label={slot.morning_time}
                                            onClick={() => !isDisabled && toggleMorningSlot(slot)}
                                            color={isSelected ? "error" : "default"}
                                            variant={isSelected ? "filled" : "outlined"}
                                            sx={{
                                                height: 40,
                                                fontSize: '0.875rem',
                                                fontWeight: (isSelected || isBooked || isPast || isBlocked) ? 600 : 400,
                                                borderColor: borderColor,
                                                bgcolor: bgColor,
                                                color: textColor,
                                                cursor: isDisabled ? 'default' : 'pointer',
                                                pointerEvents: isDisabled ? 'none' : 'auto',
                                                opacity: isDisabled ? 0.8 : 1,
                                                '&.MuiChip-root:hover': {
                                                    bgcolor: bgColor,
                                                    backgroundColor: bgColor,
                                                },
                                                "&.Mui-disabled": {
                                                    bgcolor: bgColor,
                                                    color: textColor,
                                                    opacity: 1,
                                                }
                                            }}
                                        />
                                    );
                                })}
                                {!slotData.morning_slots?.length && (
                                    <Typography variant="body2" color="textSecondary" sx={{ gridColumn: '1 / -1' }}>No morning slots available.</Typography>
                                )}
                            </Box>

                            <Typography variant="subtitle2" sx={{ color: 'primary.main', mb: 1.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                                Evening Slots {slotData.evening_slots?.length > 0 ? `(${slotData.slot_timing} Mins)` : ''}
                            </Typography>

                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                                gap: 1.5
                            }}>
                                {slotData.evening_slots?.map((slot, idx) => {
                                    const fallbackTime = slot.evening_time || slot.morning_time;
                                    const isSelected = selectedEveningSlots.includes(fallbackTime);
                                    const isInitialBlocked = initialBlockedTimes.includes(fallbackTime);

                                    // Logic for different states
                                    const isBooked = slot.is_booked === 1;
                                    const isPast = slot.is_booked === 2;
                                    const isBlocked = slot.is_booked === 3;

                                    // Determine colors
                                    let bgColor = isSelected ? "error.main" : "white";
                                    let textColor = isSelected ? "white" : "inherit";
                                    let borderColor = isSelected ? "error.main" : "divider";
                                    let isDisabled = false;

                                    if (isBooked && !isInitialBlocked) {
                                        bgColor = '#CBD5E1';
                                        textColor = '#475569';
                                        borderColor = '#CBD5E1';
                                        isDisabled = true;
                                    } else if (isPast && !isInitialBlocked) {
                                        bgColor = '#FECACA';
                                        textColor = '#991B1B';
                                        borderColor = '#FECACA';
                                        isDisabled = true;
                                    } else if (isBlocked && !isInitialBlocked && !isSelected) {
                                        bgColor = '#000000e6';
                                        textColor = 'white';
                                        borderColor = '#000000e6';
                                        isDisabled = true;
                                    }

                                    return (
                                        <Chip
                                            key={`evening-${idx}`}
                                            label={fallbackTime}
                                            onClick={() => !isDisabled && toggleEveningSlot(slot)}
                                            color={isSelected ? "error" : "default"}
                                            variant={isSelected ? "filled" : "outlined"}
                                            sx={{
                                                height: 40,
                                                fontSize: '0.875rem',
                                                fontWeight: (isSelected || isBooked || isPast || isBlocked) ? 600 : 400,
                                                borderColor: borderColor,
                                                bgcolor: bgColor,
                                                color: textColor,
                                                cursor: isDisabled ? 'default' : 'pointer',
                                                pointerEvents: isDisabled ? 'none' : 'auto',
                                                opacity: isDisabled ? 0.8 : 1,
                                                '&.MuiChip-root:hover': {
                                                    bgcolor: bgColor,
                                                    backgroundColor: bgColor,
                                                },
                                                "&.Mui-disabled": {
                                                    bgcolor: bgColor,
                                                    color: textColor,
                                                    opacity: 1,
                                                }
                                            }}
                                        />
                                    );
                                })}
                                {!slotData.evening_slots?.length && (
                                    <Typography variant="body2" color="textSecondary" sx={{ gridColumn: '1 / -1' }}>No evening slots available.</Typography>
                                )}
                            </Box>
                        </Box>
                    </Grid>
                )}

                {slotData && (
                    <Grid item xs={12}>
                        <Stack direction="row" justifyContent="end" sx={{ mt: 2 }}>
                            <Button
                                variant="contained"
                                onClick={onSave}
                                disabled={loading}
                                sx={{
                                    height: 40,
                                    borderRadius: 3,
                                    m: 1,
                                    backgroundColor: color.button,
                                    "&:hover": { backgroundColor: color.button },
                                }}
                            >
                                Save Blocked Slots
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (props.postSubmit) props.postSubmit();
                                }}
                                sx={{
                                    height: 40,
                                    borderRadius: 3,
                                    m: 1,
                                    borderColor: color.button,
                                    color: color.button,
                                    "&:hover": { color: color.button },
                                }}
                            >
                                Cancel
                            </Button>
                        </Stack>
                    </Grid>
                )}
            </Grid>
        </React.Fragment>
    );
}
